import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser, useSupabaseClient } from "@supabase/auth-helpers-react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface AuthGuardProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  allowModerator?: boolean;
}

export const AuthGuard = ({ children, requireAdmin = false, allowModerator = false }: AuthGuardProps) => {
  const navigate = useNavigate();
  const user = useUser();
  const supabase = useSupabaseClient();
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Session check error:", error);
          toast.error("حدث خطأ في التحقق من الجلسة");
          navigate("/auth");
          return;
        }

        if (!session) {
          toast.error("انتهت صلاحية الجلسة، الرجاء تسجيل الدخول مجدداً");
          navigate("/auth");
          return;
        }

        if (requireAdmin || allowModerator) {
          const { data: roles, error: rolesError } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.user.id)
            .single();

          if (rolesError) {
            toast.error("حدث خطأ في التحقق من الصلاحيات");
            navigate("/");
            return;
          }

          const userRole = roles?.role;
          
          if (requireAdmin && userRole !== 'admin') {
            toast.error("غير مصرح لك بالوصول إلى هذه الصفحة");
            navigate("/");
            return;
          }

          if (allowModerator && userRole !== 'admin' && userRole !== 'moderator') {
            toast.error("غير مصرح لك بالوصول إلى هذه الصفحة");
            navigate("/");
            return;
          }
        }
      } finally {
        setIsCheckingSession(false);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {
      if (event === 'SIGNED_OUT') {
        toast.error("تم تسجيل خروجك من النظام");
        navigate("/auth");
      } else if (event === 'TOKEN_REFRESHED') {
        toast.success("تم تجديد الجلسة بنجاح");
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [user, navigate, requireAdmin, allowModerator, supabase]);

  if (isCheckingSession) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return <>{children}</>;
};